/**
 * One prompt group, then a random rule or any number of its rules. The ✕
 * shows when the row can be removed (the Errors tab's swap picks one only).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { X } from 'lucide-react';

import { SearchableSelect } from '@/components/ui';
import type { TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';
import { getActiveSelectableLeafGroupsUnderParent } from '@/features/campaign-texts/utils/group-tree.utils';

import type { PromptPick } from './prompt-group-picks';
import { RuleSelect } from './rule-select';

interface Option { id: string; name: string }

export const PickRow: FC<{
  groups: TreeGroupLike[];
  folders: Option[];
  pick: PromptPick;
  onChange: (pick: PromptPick) => void;
  onRemove?: () => void;
}> = ({ groups, folders, pick, onChange, onRemove }) => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="min-w-72 flex-[1.4]">
      <SearchableSelect
        options={withCurrent(folders, pick.folderId, groups).map(asOption)}
        value={pick.folderId}
        onChange={(folderId) => onChange({ folderId, ruleIds: [] })}
        placeholder="Prompt group"
        searchPlaceholder="Search prompt groups…"
      />
    </div>
    <div className="min-w-44 flex-1">
      <RuleSelect
        rules={rulesFor(groups, pick)}
        ruleIds={pick.ruleIds}
        onChange={(ruleIds) => onChange({ ...pick, ruleIds })}
      />
    </div>
    {onRemove ? (
      <button type="button" aria-label="Remove group" onClick={onRemove} className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
        <X className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);

function asOption(row: Option): { value: string; label: string } {
  return { value: row.id, label: row.name };
}

function withCurrent(folders: Option[], folderId: string, groups: TreeGroupLike[]): Option[] {
  // an empty pick (the swap's, before a choice) shows the placeholder, never a "Saved rule"
  if (!folderId || folders.some((f) => f.id === folderId)) return folders;
  return [...folders, { id: folderId, name: nameOf(groups, folderId) }];
}

function nameOf(groups: TreeGroupLike[], id: string): string {
  return groups.find((g) => g.id === id)?.name ?? 'Saved rule';
}

function rulesFor(groups: TreeGroupLike[], pick: PromptPick): Option[] {
  const rules = getActiveSelectableLeafGroupsUnderParent(groups, pick.folderId);
  const listed = new Set(rules.map((rule) => rule.id));
  const extra = pick.ruleIds
    .filter((id) => !listed.has(id))
    .map((id) => ({ id, name: nameOf(groups, id) }));
  return [...rules.map((rule) => ({ id: rule.id, name: rule.name })), ...extra];
}
